import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios';
import { EdgeStack, StatusType } from '@/react/edge/edge-stacks/types';
import { withGlobalError } from '@/react-tools/react-query';

import { EdgeUpdateResponse, StatusType as ScheduleStatusType } from '../types';

import { queryKeys } from './query-keys';
import { buildUrl } from './urls';

export type EdgeUpdateListItemResponse = EdgeUpdateResponse & {
  edgeStack?: EdgeStack;
  aggregatedStatus: Partial<Record<StatusType, number>>;
  statusMessage: string;
  status: ScheduleStatusType;
};

async function getList(
  edgeComputeEnabled?: boolean,
  includeEdgeStacks?: boolean
) {
  try {
    if (!edgeComputeEnabled) {
      return [];
    }
    const { data } = await axios.get<EdgeUpdateListItemResponse[]>(buildUrl(), {
      params: { includeEdgeStacks },
    });
    return data;
  } catch (err) {
    throw parseAxiosError(
      err as Error,
      'Failed to get list of edge update schedules'
    );
  }
}

export function useList<T = Array<EdgeUpdateListItemResponse>>({
  select,
  refetchInterval,
  edgeComputeEnabled = true,
  includeEdgeStacks = false,
}: {
  select?: (stacks: EdgeUpdateListItemResponse[]) => T;
  refetchInterval?: number | false | ((data?: T) => false | number);
  edgeComputeEnabled?: boolean;
  includeEdgeStacks?: boolean;
} = {}) {
  return useQuery(
    queryKeys.list(includeEdgeStacks),
    () => getList(edgeComputeEnabled, includeEdgeStacks),
    {
      ...withGlobalError('Failed loading update schedules'),
      select,
      refetchInterval,
    }
  );
}
